import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import ApprovalStatusLabel from '../../reportCards/ApprovalStatusLabel';
import BooleanFlag from '../../reportCards/BooleanFlag';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import { formatContextDate } from '../../shared/checklistUtils';
import type { ManualApprovalState } from '../../types';

import { FESTIVE_DATE_LABEL } from './blackoutRules';
import type { BlackoutChecklistRow, BlackoutCoverageSummary } from './types';

interface Props {
  coverage: BlackoutCoverageSummary;
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<BlackoutChecklistRow>;
  showAutoApproved: boolean;
  travelFrom?: string;
  travelTo?: string;
  onApprovalChange: (rowId: string) => void;
}

const APPROVED_ROW_SX = {
  '& > td': {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.08),
  },
  '&:hover > td': {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.14),
  },
  '& .MuiInputBase-root': {
    bgcolor: 'background.paper',
  },
} as const;

export default function BlackoutReportCard({
  coverage,
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  travelFrom,
  travelTo,
  onApprovalChange,
}: Props) {
  return (
    <ReportCard>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>Blackout Name</TableCell>
              <TableCell>Date Range</TableCell>
              <TableCell>Within Travel Validity</TableCell>
              <TableCell>Clash with Gala / Festive Dates</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={5} label="No blackout rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = !!manualApprovals[row.id];
                const approved = row.status === 'approved' || manuallyApproved;

                return (
                  <TableRow key={row.id} hover sx={approved ? APPROVED_ROW_SX : undefined}>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatContextDate(row.from)} - {formatContextDate(row.to)}
                    </TableCell>
                    <TableCell>
                      <BooleanFlag value={row.withinTravelValidity} />
                    </TableCell>
                    <TableCell>
                      <BooleanFlag falseIsGood value={!row.festiveClear} />
                    </TableCell>
                    <TableCell>
                      {approved ? (
                        <ApprovalStatusLabel manuallyApproved={manuallyApproved} />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => onApprovalChange(row.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        sx={{ borderBottom: 0, borderTop: 1 }}
        text={
          <>
            <Box>
              <strong>Blackout coverage rule:</strong> cumulative blackout dates must cover less than{' '}
              <strong>75%</strong> of the travel period.
            </Box>
            <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2 }}>
              <li>
                <strong>Travel dates:</strong> {formatContextDate(travelFrom)} - {formatContextDate(travelTo)}
                {coverage.travelDays ? ` (${coverage.travelDays} total days)` : ''}
              </li>
              <li>
                <strong>Total blackout days:</strong> {coverage.blackoutDays} ({coverage.percent}% of travel period)
                <Box
                  component="span"
                  aria-label={
                    coverage.status === 'approved'
                      ? 'Total blackout percentage is within range'
                      : 'Total blackout percentage is outside range'
                  }
                  title={
                    coverage.status === 'approved'
                      ? 'Total blackout percentage is within range'
                      : 'Total blackout percentage is outside range'
                  }
                  sx={{
                    bgcolor: coverage.status === 'approved' ? 'success.main' : 'warning.main',
                    borderRadius: '50%',
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.04)',
                    display: 'inline-block',
                    height: 10,
                    ml: 1,
                    verticalAlign: '-1px',
                    width: 10,
                  }}
                />
              </li>
              <li>
                <strong>Festive dates checked:</strong> {FESTIVE_DATE_LABEL}. Rows that overlap any of these dates
                require approval.
              </li>
            </Box>
          </>
        }
      />
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="blackout row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
